import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { differenceInDays } from 'date-fns'
import { useLocalStorage } from 'usehooks-ts'

import companies from 'modules/core/store/collections/companies'

import {
  companyInFreeTrial,
  companyIsSelfServed,
  companySubscription,
} from 'modules/billing/store/collections/subscriptions'
import type { MeetingData } from 'modules/onboarding/components/HelpBox/HubspotModal/types'

import useHubspotVariables from './useHubspotVariables'

const useOnboardingMeetingData = () => {
  const isAdmin = false
  const currentCompany = companies.current
  const { isOnboardingSegment } = useHubspotVariables()
  const DAYS_TO_SHOW_BANNER = 14

  const [meetingData, setMeetingData] = useLocalStorage<Partial<MeetingData>>(
    LOCAL_STORAGE_KEYS.onboardingMeetingData,
    {
      meetingBookSucceeded: false,
      hideOnboardingBanner: false,
    }
  )

  /*
    Rules:
    - Show the onboarding meeting banner to self-served companies
    - Show only to the company admin
    - Show only if company is in segment that can be offered meetings.
    - Hide the onboarding meeting banner if subscription is 2weeks old
    - Hide the onboarding meeting banner if the meeting is booked
    - Hide the onboarding meeting banner if the user has hidden it
  */
  const shouldHideOnboardingBanner = () => {
    if (
      companyInFreeTrial(currentCompany) ||
      !companyIsSelfServed(currentCompany) ||
      !isAdmin ||
      !isOnboardingSegment
    )
      return true

    if (meetingData?.hideOnboardingBanner) return true

    const subscription = companySubscription(currentCompany)

    if (subscription && subscription.inPaidPlan) {
      const subCreatedAt = new Date(subscription.attributes.created_at * 1000)
      const diffDays = differenceInDays(new Date(), subCreatedAt)
      if (diffDays >= DAYS_TO_SHOW_BANNER) return true
    }

    return false
  }

  const writeData = (newData: Partial<MeetingData>) => {
    setMeetingData({ ...meetingData, ...newData })
  }

  return {
    meetingData,
    writeData,
    shouldHideOnboardingBanner: shouldHideOnboardingBanner(),
  }
}

export default useOnboardingMeetingData
